import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { parseDate } from '../components/Cronograma/utils';

export const useCronogramaFilters = (cursosData, loading) => {
    const currentYear = dayjs().year().toString();
    const [filteredData, setFilteredData] = useState([]);
    const [ministerioFilter, setMinisterioFilter] = useState('all');
    const [areaFilter, setAreaFilter] = useState('all');
    const [nombreFilter, setNombreFilter] = useState('');
    const [yearFilter, setYearFilter] = useState(currentYear);
    const [monthFilter, setMonthFilter] = useState('all');
    const [activosFilterActive, setActivosFilterActive] = useState(false);
    const [asignadoFilter, setAsignadoFilter] = useState('');
    const [omitirCancelados, setOmitirCancelados] = useState(false);
    const [plataformaFilter, setPlataformaFilter] = useState('all');
    const [autogestionadoFilter, setAutogestionadoFilter] = useState('all');
    const [areaOptions, setAreaOptions] = useState(['all']);

    const availableYears = useMemo(() => {
        if (!cursosData || cursosData.length === 0) return [currentYear];
        const years = new Set();
        cursosData.forEach(c => {
            const d = parseDate(c["Fecha inicio del curso"]);
            if (d && d.isValid()) years.add(d.year().toString());
        });
        const arr = Array.from(years).sort((a, b) => b.localeCompare(a));
        if (!arr.includes(currentYear)) arr.unshift(currentYear);
        return arr;
    }, [cursosData, currentYear]);

    useEffect(() => {
        if (loading || !cursosData.length) {
            setAreaOptions(['all']);
            if (areaFilter !== 'all') setAreaFilter('all');
            return;
        }
        let relevantCourses = [];
        if (ministerioFilter === 'all') {
            relevantCourses = cursosData;
        } else {
            relevantCourses = cursosData.filter(c => c["ADM"] === ministerioFilter);
        }
        const areasSet = new Set(relevantCourses.map(c => c["Area"]).filter(Boolean));
        const newAreaOptions = ['all', ...Array.from(areasSet).sort((a, b) => a.localeCompare(b))];
        setAreaOptions(newAreaOptions);

        if (ministerioFilter !== 'all' && !newAreaOptions.includes(areaFilter)) {
            setAreaFilter('all');
        }
    }, [ministerioFilter, cursosData, loading, areaFilter]);

    useEffect(() => {
        if (loading && cursosData.length === 0) {
            setFilteredData([]);
            return;
        };

        let data = (cursosData || []).filter(Boolean);
        const today = dayjs().startOf('day');

        if (ministerioFilter !== 'all') data = data.filter(c => c && c["ADM"] === ministerioFilter);
        if (areaFilter !== 'all') data = data.filter(c => c && c["Area"] === areaFilter);
        if (nombreFilter.trim()) {
            const term = nombreFilter.trim().toLowerCase();
            data = data.filter(c => Boolean(c) && (
                (c["Nombre del curso"] && String(c["Nombre del curso"]).toLowerCase().includes(term)) ||
                (c["Código del curso"] && String(c["Código del curso"]).toLowerCase().includes(term))
            ));
        }
        if (yearFilter !== 'all') {
            data = data.filter(c => {
                if (!c) return false;
                const parsedDate = parseDate(c["Fecha inicio del curso"]);
                return parsedDate && parsedDate.isValid() && parsedDate.year().toString() === yearFilter;
            });
        }
        if (monthFilter !== 'all') {
            const targetMonth = parseInt(monthFilter, 10);
            data = data.filter(c => {
                if (!c) return false;
                const parsedDate = parseDate(c["Fecha inicio del curso"]);
                return parsedDate && parsedDate.month() === targetMonth;
            });
        }
        if (plataformaFilter !== 'all') {
            data = data.filter(c => {
                if (!c) return false;
                const plat = c["Plataforma"] || c["plataforma_dictado"] || c.originalInstancia?.plataforma_dictado || c.originalInstancia?.detalle_curso?.plataforma_dictado;
                return plat === plataformaFilter;
            });
        }
        if (autogestionadoFilter !== 'all') {
            data = data.filter(c => {
                if (!c) return false;
                const expected = autogestionadoFilter === 'si' ? 'Sí' : 'No';
                return c["Es Autogestionado"] === expected;
            });
        }
        if (activosFilterActive) {
            data = data.filter(c => {
                if (!c) return false;
                const startDate = parseDate(c["Fecha inicio del curso"]);
                const endDate = parseDate(c["Fecha fin del curso"]);
                return startDate && endDate && startDate.isSameOrBefore(today) && endDate.isSameOrAfter(today);
            });
        }
        if (omitirCancelados) {
            data = data.filter(c => c && c["Estado"] !== 'CANC');
        }
        if (asignadoFilter.trim()) {
            const term = asignadoFilter.trim().toLowerCase();
            data = data.filter(c => c && c["Asignado"] && String(c["Asignado"]).toLowerCase().includes(term));
        }
        setFilteredData(data);
    }, [cursosData, ministerioFilter, areaFilter, nombreFilter, yearFilter, monthFilter, plataformaFilter, autogestionadoFilter, activosFilterActive, asignadoFilter, omitirCancelados, loading]);

    const handleClearFilters = () => {
        setMinisterioFilter('all');
        setAreaFilter('all');
        setNombreFilter('');
        setYearFilter(currentYear);
        setMonthFilter('all');
        setPlataformaFilter('all');
        setAutogestionadoFilter('all');
        setActivosFilterActive(false);
        setAsignadoFilter('');
        setOmitirCancelados(false);
    };

    const isFilterActive = useMemo(() =>
        nombreFilter ||
        ministerioFilter !== 'all' ||
        areaFilter !== 'all' ||
        yearFilter !== currentYear ||
        monthFilter !== 'all' ||
        plataformaFilter !== 'all' ||
        autogestionadoFilter !== 'all' ||
        activosFilterActive ||
        asignadoFilter ||
        omitirCancelados,
        [nombreFilter, ministerioFilter, areaFilter, yearFilter, currentYear, monthFilter, plataformaFilter, autogestionadoFilter, activosFilterActive, asignadoFilter, omitirCancelados]
    );

    return {
        filteredData,
        filters: {
            ministerioFilter,
            areaFilter,
            nombreFilter,
            yearFilter,
            monthFilter,
            plataformaFilter,
            autogestionadoFilter,
            activosFilterActive,
            asignadoFilter,
            omitirCancelados,
        },
        setFilters: {
            setMinisterioFilter,
            setAreaFilter,
            setNombreFilter,
            setYearFilter,
            setMonthFilter,
            setPlataformaFilter,
            setAutogestionadoFilter,
            setActivosFilterActive,
            setAsignadoFilter,
            setOmitirCancelados,
        },
        availableYears,
        areaOptions,
        handleClearFilters,
        isFilterActive,
    };
};